import { round } from "lodash-es";
import { defineStore } from "pinia";
import { ref } from "vue";

import { loadReactiveUser } from "@/backend/http/user";
import { captureMessage } from "@/error/sentry";
import type { Sticker } from "@/model/StickerType";
import type { AlmSourceId, Color, Id, IdMap, StatusClass } from "@/model/baseTypes";
import type { Board, BoardId } from "@/model/board";
import type { BoardCard, Card, CardMeta, LinkedSticky, Reaction, Reactions } from "@/model/card";
import { reactions, roundPoints } from "@/model/card";
import type { RelativeCoordinate } from "@/model/coordinates";
import { relativeCoord } from "@/model/coordinates";
import type { Team } from "@/model/session";
import type { StatusClassDistribution, StatusDistributionState } from "@/model/status";
import type { StickyType } from "@/model/stickyType";
import { isDependency } from "@/model/stickyType";
import type { PersonalUser } from "@/model/user";
import { firstValue } from "@/utils/record";
import { removeNonPrintable } from "@/utils/string";
import { asSemiReadonly } from "@/utils/vue";

import { useAlmItemTypeStore } from "./almItemType";
import { useBoardStore } from "./board";
import { useBoardsStore } from "./boards";
import { useLinkStore } from "./link";
import { isStickyTypeAlmSynced, useStickyTypeStore } from "./stickyType";
import { useTeamStore } from "./team";
import { useUserStore } from "./user";

export type CardEvent = Id & { pos: RelativeCoordinate } & BoardId &
  Partial<Card> &
  Pick<
    Card,
    "type" | "priority" | "teamId" | "iterationId" | "flagType" | "objectives" | "reactions"
  > &
  Pick<CardMeta, "zIndex">;

export const useCardStore = defineStore(
  "card",
  () => {
    const cards = ref<IdMap<Card>>({});

    function add(cardProperties: CardEvent, board?: Board) {
      const id = cardProperties.id;
      // When we create a new sticky, we also get an event from the backend.
      // So this function is always called twice, ignore the second.
      if (cards.value[id] && (!board || board.cards[id])) {
        return id;
      }
      const card: Card = {
        id,
        groupId: cardProperties.groupId,
        status: cardProperties.status,
        text: cardProperties.text || "",
        points: roundPoints(cardProperties.points),
        priority: cardProperties.priority,
        iterationId: cardProperties.iterationId === undefined ? null : cardProperties.iterationId,
        editor: null,
        type: cardProperties.type,
        flagType: cardProperties.flagType,
        almId: cardProperties.almId || "",
        almSourceId: cardProperties.almSourceId ?? null,
        almIssueUrl: cardProperties.almIssueUrl,
        teamId: cardProperties.teamId || null,
        precondTeam: cardProperties.precondTeam,
        dependTeam: cardProperties.dependTeam,
        risk: cardProperties.risk,
        objectives: cardProperties.objectives || [],
        reactions: cardProperties.reactions,
        artId: cardProperties.artId || null,
        assignee: cardProperties.assignee || null,
        reporter: cardProperties.reporter || null,
        linkedStickies: cardProperties.linkedStickies || [],
        statusDistribution: cardProperties.statusDistribution,
        statusDistributionState: cardProperties.statusDistributionState,
      };
      const cardMeta: CardMeta = {
        pos: cardProperties.pos,
        zIndex: cardProperties.zIndex || (board?.maxZ ?? 0),
        mark: "normal",
        editing: false,
        dragging: false,
        isHighlighted: false,
        isRelatedToHighlighted: false,
        dependencyTeamFilter: null,
      };
      cards.value[id] = card;
      if (board) {
        board.cards[id] = { data: card, meta: cardMeta };
        if (cardProperties.zIndex > board.maxZ) {
          board.maxZ = cardProperties.zIndex;
        }
        if (cardProperties.groupId) {
          useLinkStore().addToLinkGroup(
            cardProperties.groupId,
            cardProperties.boardId,
            board.cards[id],
          );
        }
      }
      setType({ id, type: cardProperties.type.id, boardId: cardProperties.boardId });
      if (cardProperties.almSourceId !== undefined) {
        setAlmSource({ id, almSourceId: cardProperties.almSourceId });
      }
      return id;
    }

    function setAlmSource(e: Id & { almSourceId: AlmSourceId | null }) {
      const card = cards.value[e.id];
      card.almSourceId = e.almSourceId ?? null;
    }

    function setPriority(e: Id & { priority: number; priorities?: boolean }) {
      const prios = cards.value[e.id].type.priorities;
      const use = e.priorities === undefined ? !!prios : e.priorities;
      const priority = use ? e.priority || firstValue(prios!).value : round(e.priority || 0, 2);
      cards.value[e.id].priority = priority;
      return { use, priority };
    }

    function setIteration(e: Id & { iterationId: number | null }) {
      const card = cards.value[e.id];
      if (card.iterationId !== e.iterationId) {
        card.iterationId = e.iterationId;
        return true;
      }
    }

    function setTeam(e: Id & { teamId: string | null }) {
      const card = cards.value[e.id];
      card.teamId = e.teamId;
    }

    function setArt(e: Id & { artId: string | null }) {
      const card = cards.value[e.id];
      card.artId = e.artId;
    }

    function setDependAction(e: Id & { dependTeam: Team }) {
      const board = useBoardsStore().boardByTypeOptional("team", { teamId: e.dependTeam.id });
      const card = cards.value[e.id];
      card.flagType = { icon: "flag", color: "red" };
      card.precondTeam = useTeamStore().current;
      card.dependTeam = e.dependTeam;
      return { board, card };
    }

    function setType(e: Id & { boardId?: string; type: string }) {
      const card = cards.value[e.id];
      const board = e.boardId
        ? (useBoardsStore().boards[e.boardId] as Board | undefined)
        : useBoardStore().currentBoard();
      const type = useStickyTypeStore().findStickyType(e.type);
      const changes: { board: Board | undefined; props: Partial<Card>; priorities: boolean } = {
        board,
        props: { type, teamId: card.teamId, iterationId: card.iterationId },
        priorities: false,
      };
      if (card.type && card.type.priorities !== type.priorities) {
        const { priority, use } = setPriority({
          id: e.id,
          priority: type.priorities ? firstValue(type.priorities).value : 0,
          priorities: !!type.priorities,
        });
        changes.props.priority = priority;
        changes.priorities = use;
      }
      card.type = type;
      if (board?.type === "team" && card.type.origin === "backlog") {
        const backlogAlmSources = useBoardsStore().boardByType("backlog").almSources;
        if (backlogAlmSources.length === 1) {
          changes.props.almSourceId = backlogAlmSources[0].id;
        } else if (backlogAlmSources.length === 0) {
          changes.props.almSourceId = null;
        }
      }
      if (type.functionality === "dependency" && card.teamId && !card.precondTeam) {
        card.precondTeam = useTeamStore().findTeam({ id: card.teamId });
        changes.props.precondTeam = card.precondTeam;
      }
      if (type.functionality === "workitem" && !card.status) {
        card.status = useAlmItemTypeStore().calcStatus(
          undefined,
          type,
          card.teamId,
          board?.artId,
          card.almSourceId,
        );
        changes.props.status = card.status;
      }
      if (type.functionality === "note" && card.reactions === undefined) {
        const emptyReactions = reactions.map((reaction) => [reaction, []]);
        setReactions(e.id, Object.fromEntries(emptyReactions));
      }
      if (isStickyTypeAlmSynced(type)) {
        card.text = removeNonPrintable(card.text);
        changes.props.text = card.text;
      }
      return changes;
    }

    function setObjectives(cardId: Card["id"], objectives: Card["objectives"] | undefined) {
      if (!objectives) {
        return;
      }
      const card = cards.value[cardId];
      if (!card) {
        captureMessage("Tried to set objectives but it is not in the state", {
          info: { cardId },
        });
        return;
      }
      card.objectives = [...objectives];
    }

    function setGroup(e: Id & { groupId: string | null }) {
      const card = cards.value[e.id];
      if (card) {
        card.groupId = e.groupId;
        // groupId null assumes all mirrored stickies are/will be removed
        if (e.groupId !== null) {
          for (const boardId in useBoardsStore().boards) {
            const board = useBoardsStore().boards[boardId];
            const boardCard = board.cards[e.id];
            if (boardCard) {
              useLinkStore().addToLinkGroup(e.groupId, boardId, boardCard);
            }
          }
        }
      }
    }

    function readOnly(e: Id & ({ readOnly: true; userId: string } | { readOnly: false })) {
      const card = cards.value[e.id];
      if (card) {
        // card might just have been deleted
        if (e.readOnly) {
          card.editor = loadReactiveUser({ id: e.userId, caller: "editor" });
        } else {
          card.editor = null;
        }
      }
    }

    function setStatus(
      board: Board,
      cardId: string,
      statusName: string | null,
      options: { transition?: string; statusClass?: StatusClass } = {},
    ) {
      const card = cards.value[cardId];
      const status =
        statusName === null
          ? undefined
          : useAlmItemTypeStore().findStatusForCard(statusName, card, board, options.statusClass);
      card.status = status;
      card.transition = options.transition;
      return status;
    }

    function updateCardsStatus(card: Card, board: Board) {
      if (!card.status) {
        return;
      }
      const status = useAlmItemTypeStore().findStatusForCard(
        card.status.name,
        card,
        board,
        card.status.statusClass,
      );
      for (const id in cards.value) {
        const c = cards.value[id];
        if (hasSameStatus(c, card)) {
          c.status = status;
        }
      }

      function hasSameStatus(a: Card, b: Card) {
        return (
          a.status?.id === b.status?.id &&
          a.type.id === b.type.id &&
          a.teamId === b.teamId &&
          a.almSourceId === b.almSourceId
        );
      }
    }

    function setReactions(cardId: string, reactions: Reactions) {
      cards.value[cardId].reactions = reactions;
    }

    function setLinkedStickies(cardId: string, linkedStickies: LinkedSticky[]) {
      cards.value[cardId].linkedStickies = linkedStickies;
    }

    function deleteCard({ id: cardId, boardId }: Id & BoardId) {
      const card = cards.value[cardId];
      const board = useBoardsStore().boards[boardId];
      const existsOnTheBoard = board?.cards[cardId];

      if (existsOnTheBoard && useBoardStore().activeCardId === cardId) {
        useBoardStore().setActiveCardId(null);
      }
      if (board) {
        useBoardStore().unselectSticky(cardId, board);
      }
      if (!card || !existsOnTheBoard) {
        // might already be deleted
        return false;
      }
      useLinkStore().removeFromLinkGroup(card, boardId);
      delete cards.value[cardId];
      for (const b in useBoardsStore().boards) {
        delete useBoardsStore().boardById(b).cards[cardId];
      }
      return true;
    }

    function updateStickyType(type: StickyType) {
      for (const id in cards.value) {
        const card = cards.value[id];
        if (card.type.id === type.id) {
          card.type = type;
        }
      }
    }

    return {
      cards: asSemiReadonly(cards),

      /**
       * Returns the board card corresponding to the given card's *origin* card.
       * Falls back to a synthetic BoardCard when no origin board exists
       * (e.g. workitems accidentally belonging to an external team)
       * so callers like the breakdown view can still render them.
       */
      originBoardCard: (card: Card): BoardCard => {
        const board = useBoardsStore().boardByOriginCard(card);
        return board?.cards[card.id] ?? orphanBoardCard(card);
      },
      hasCurrentUserReaction: (cardId: string, reaction: Reaction): boolean =>
        cards.value[cardId].reactions?.[reaction]?.some(
          (user) => user.id === useUserStore().technicalUser.id,
        ) ?? false,

      add,
      setAlmSource,
      setPriority,
      setPoints: (e: Id & { points: number }) => (cards.value[e.id].points = roundPoints(e.points)),
      setFlag: (e: Id & { flagType: Sticker | null }) => (cards.value[e.id].flagType = e.flagType),
      setRisk: (e: Id & Pick<Card, "risk">) => (cards.value[e.id].risk = e.risk),
      setText: (e: Id & { text: string }) => (cards.value[e.id].text = e.text),
      setIteration,
      setTeam,
      setArt,
      setPrecondTeam: (e: Id & { teamId?: string | null; teamName?: string | null }) =>
        (cards.value[e.id].precondTeam = useTeamStore().findTeam({
          id: e.teamId,
          name: e.teamName,
        })),
      setDependTeam: (e: Id & { teamId?: string | null; teamName?: string | null }) =>
        (cards.value[e.id].dependTeam = useTeamStore().findTeam({
          id: e.teamId,
          name: e.teamName,
          withExternal: true,
        })),
      setDependAction,
      setType,
      setObjectives,
      setGroup,
      setAlmId: (e: Id & { almId: string }) => (cards.value[e.id].almId = e.almId),
      setAlmIssueUrl: (e: Id & { almIssueUrl: string | null }) =>
        (cards.value[e.id].almIssueUrl = e.almIssueUrl ?? undefined),
      readOnly,
      setStatus,
      updateCardsStatus,
      setReactions,
      setAssignee: (cardId: string, user: PersonalUser | null) =>
        (cards.value[cardId].assignee = user),
      setReporter: (cardId: string, user: PersonalUser | null) =>
        (cards.value[cardId].reporter = user),
      setLinkedStickies,
      setStatusDistribution: (
        cardId: string,
        statusDistribution?: StatusClassDistribution,
        state?: StatusDistributionState | null,
      ) => {
        cards.value[cardId].statusDistribution = statusDistribution;
        cards.value[cardId].statusDistributionState = state;
      },
      delete: deleteCard,
      updateStickyType,
    };
  },
  { autoReset: true },
);

export function mirrorOriginId(card: Card) {
  if (card.groupId && card.groupId !== card.id) {
    return card.groupId;
  }
}

export function isCardFromTeam(card: Card, teamId?: string) {
  return card.type.origin === "team" && !!teamId && teamId === card.teamId;
}

type CardColorInput = Pick<Card, "type"> & Partial<Pick<Card, "teamId" | "artId" | "precondTeam">>;

/**
 * Get the color of a card on a given board.
 * @returns The color of the card.
 */
export function cardColor(card: CardColorInput, board: Board): Color {
  if (board.type === "backlog" && card.type.origin === "backlog") {
    return card.teamId ? card.type.altColor : card.type.color;
  }

  if (board.type === "solution_backlog" && card.type.origin === "solution_backlog") {
    return card.artId ? card.type.altColor : card.type.color;
  }

  if (isDependency(card)) {
    return board.type === "team" && card.precondTeam?.id === board.team.id
      ? card.type.color
      : card.type.altColor;
  }

  return card.type.color;
}

/**
 * Renders a synthetic board card when none exists
 * (e.g. 'origin card' for workitems belonging to an external team)
 */
function orphanBoardCard(card: Card): BoardCard {
  return {
    data: card,
    meta: {
      pos: relativeCoord(0, 0),
      zIndex: 0,
      mark: "normal",
      editing: false,
      dragging: false,
      isHighlighted: false,
      isRelatedToHighlighted: false,
      dependencyTeamFilter: null,
    },
  };
}
